import React, { useEffect, useRef, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import Content from 'studio/components/Content/Content';
import DataGrid from 'studio/components/DataGrid/DataGrid';
import styles from './Synchronization.styles';
import services from 'services';
import Typography from '@mui/material/Typography';
import SyncIcon from '@mui/icons-material/SyncRounded';
import PlayArrowIcon from '@mui/icons-material/PlayArrowRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { formatDate, formatDuration, pluralize as plrl, sleep } from 'studio/helpers';
import classNames from 'classnames';
import ScheduleForm from './ScheduleForm/ScheduleForm';
import MoreHorizIcon from '@mui/icons-material/MoreHorizRounded';
import ScheduleIcon from '@mui/icons-material/ScheduleRounded';
import HelpIcon from '@mui/icons-material/HelpOutlineRounded';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import { useLoader } from 'components/loaders/FadeLoader';
import { useMessage } from 'studio/components/Message';
import OverflowableContent from 'studio/components/OverflowableContent/OverflowableContent';
import Synchronizable from './Synchronizable/Synchronizable';
import { trackEvent } from 'utils/eventTracking';
import { partition } from 'utils/arrayUtils';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { veezooUrls } from 'config/constants';
import PageTitle from 'studio/components/PageTitle';
import { KgConceptTypes } from 'studio/pages/Editor/ConceptTree/ConceptTree';

// stores the handle to the timeout and interval used to implement the duration countdown
let durationCountdownHandle;

export const syncPolicies = {
  ignore: 'ignore',
  replace: 'replace',
  merge: 'merge'
};

// retrieves the concepts of the specified knowledge graph that can be synchronized (note that these
// also include concepts with sync policy 'ignore', which shouldn't - and can't - be synchronized)
export const getSynchronizableConcepts = async kgId => {
  // retrieve all concepts which can be synchronized
  const promises = [
    services.getKgConcepts(kgId, KgConceptTypes.class),
    services.getKgConcepts(kgId, KgConceptTypes.coOccurrence)
  ];
  const responses = await Promise.all(promises);
  return responses.flatMap(response => response.data.data).filter(concept => concept.data.isSynchronizable);
};

// text and tooltip to show when a synchronization synchronizes all concepts
export const allConceptsText = (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    All concepts with a sync policy
    <Tooltip
      title="Only concepts which can actually be synchronized and which have a sync policy other than 'ignore' are synchronized."
      placement="top"
      arrow
    >
      <IconButton size="small" style={{ marginLeft: 4 }}>
        <HelpIcon style={{ width: 20, height: 20 }} />
      </IconButton>
    </Tooltip>
  </div>
);

const Synchronization = ({ classes, knowledgeGraph }) => {
  useEffect(() => {
    trackEvent('Studio Synchronization Opened');
  }, []);

  const { withLoader } = useLoader();
  const { successMessage, errorMessage } = useMessage();
  const { showConfirmationModal } = useConfirmationModal();

  // references the content element
  const contentRef = useRef(null);

  // the synchronization schedules
  const [schedules, setSchedules] = useState(null);
  // the selection model keeping track of selected synchronization schedules
  const [scheduleSelectionModel, setScheduleSelectionModel] = useState([]);
  // specifies whether to show the schedule dialog
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  // the synchronization schedule to edit
  const [scheduleToEdit, setScheduleToEdit] = useState(null);
  // the synchronizations
  const [synchronizations, setSynchronizations] = useState(null);
  // the selection model keeping track of selected synchronizations
  const [synchronizationSelectionModel, setSynchronizationSelectionModel] = useState([]);
  // stores the duration until the start of a scheduled synchronization, to be counted down and displayed in a tooltip
  const [durationUntilStart, setDurationUntilStart] = useState(null);

  // the ID of the knowledge graph
  const kgId = knowledgeGraph?.data.id;

  const startDurationCountdown = nextScheduledDate => {
    const now = new Date();
    // the duration until the start of the next scheduled synchronization
    const duration = Math.floor((nextScheduledDate - now.getTime()) / 1000) * 1000;
    setDurationUntilStart(duration);
    stopDurationCountdown();
    durationCountdownHandle = setTimeout(() => {
      durationCountdownHandle = setInterval(() => setDurationUntilStart(duration => duration - 1000), 1000);
    }, 1000 - now.getMilliseconds());
  };

  const stopDurationCountdown = () => {
    clearTimeout(durationCountdownHandle);
    clearInterval(durationCountdownHandle);
  };

  const openScheduleDialog = scheduleId => {
    const schedule = schedules.find(schedule => schedule.data.id === scheduleId);
    setScheduleToEdit(schedule);
    setShowScheduleDialog(true);
  };
  const closeScheduleDialog = () => {
    setShowScheduleDialog(false);
    setScheduleToEdit(null);
  };

  const reloadSchedules = async () => {
    const response = await withLoader(services.getSynchronizationSchedules(kgId));
    setSchedules(response.data.data);
  };

  const reloadSynchronizations = async () => {
    const response = await withLoader(services.getSynchronizations(kgId));
    const synchronizations = response.data.data;
    setSynchronizations(synchronizations);
  };

  // resets and loads state whenever the knowledge graph changes
  useEffect(() => {
    if (kgId) {
      setSchedules(null);
      setSynchronizations(null);
      setScheduleSelectionModel([]);
      setShowScheduleDialog(false);
      setScheduleToEdit(null);
      reloadSchedules();
      reloadSynchronizations();
    }
  }, [kgId]);

  // renders a data grid cell containing synchronizables
  const renderSynchronizablesCell = (synchronizables, overflowPopperTitle) => (
    <OverflowableContent
      className={classes.synchronizables}
      tooltip={`Show all ${overflowPopperTitle.toLowerCase()}`}
      popperTitle={overflowPopperTitle}
      popperBoundariesElementRef={contentRef}
      content={isPopper =>
        (synchronizables || []).map(synchronizable => (
          <Synchronizable
            key={synchronizable.uri}
            synchronizable={synchronizable}
            // display the tooltip to the left when inside the popper for more readability
            tooltipPlacement={isPopper && 'left'}
          />
        ))
      }
    />
  );

  // the columns for the data grid containing the synchronization schedules
  const scheduleColumns = [
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 170,
      valueFormatter: params => formatDate(params.value)
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      width: 120
    },
    {
      field: 'nextScheduledDate',
      headerName: 'Next Scheduled Date',
      width: 220,
      renderCell: params => (
        <>
          {params.value ? (
            <Tooltip
              title={durationUntilStart <= 0 ? 'Started' : `Starts in ${formatDuration(durationUntilStart, true)}`}
              placement="top"
              arrow
              onOpen={() => startDurationCountdown(params.value)}
              onClose={() => stopDurationCountdown()}
            >
              <div className={classes.scheduledDate}>
                <ScheduleIcon /> {formatDate(params.value)}
              </div>
            </Tooltip>
          ) : (
            <>No synchronizations scheduled</>
          )}
          {params.row.nextScheduledDates.length > 1 && (
            <OverflowableContent
              className={classes.overflow}
              tooltip="Show more scheduled dates"
              popperTitle="Next scheduled dates"
              popperBoundariesElementRef={contentRef}
            >
              {params.row.nextScheduledDates.slice(1, 5).map(nextScheduledDate => (
                <div className={classes.scheduledDate} key={nextScheduledDate}>
                  <ScheduleIcon /> {formatDate(nextScheduledDate)}
                </div>
              ))}
              {params.row.nextScheduledDates.length > 5 && (
                <div className={classes.scheduledDate}>
                  <MoreHorizIcon />
                </div>
              )}
            </OverflowableContent>
          )}
        </>
      ),
      sortComparator: (v1, v2) => {
        if (typeof v1 === 'number') {
          return typeof v2 === 'number' ? v1 - v2 : -1;
        } else {
          return typeof v2 === 'number' ? 1 : 0;
        }
      }
    },
    {
      field: 'synchronizables',
      headerName: 'Concepts',
      flex: 1,
      renderCell: params => (params.value ? renderSynchronizablesCell(params.value, 'Concepts') : allConceptsText)
    }
  ];

  // the rows for the data grid containing the synchronization schedules
  const scheduleRows = (schedules || []).map(schedule => ({
    id: schedule.data.id,
    creationDate: schedule.data.creationDate,
    startDate: schedule.data.startDate,
    frequency: schedule.data.frequency,
    nextScheduledDate: schedule.data.nextDates?.[0],
    nextScheduledDates: schedule.data.nextDates,
    synchronizables: schedule.data.synchronizables
  }));

  // the columns for the data grid containing the synchronizations
  const synchronizationColumns = [
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 170,
      valueFormatter: params => formatDate(params.value)
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 105
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 105,
      renderCell: params => {
        const errorMessage = params.row.errorMessage;
        return (
          <>
            <div>
              <span className={classNames(classes.status, params.value)}>{params.value}</span>
            </div>
            {!!errorMessage && (
              <OverflowableContent
                className={classes.overflow}
                tooltip="Show the error message"
                popperTitle="Error message"
                popperBoundariesElementRef={contentRef}
              >
                <pre className={classes.errorMessage}>{errorMessage}</pre>
              </OverflowableContent>
            )}
          </>
        );
      }
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 185,
      valueFormatter: params => (params.value >= 0 ? formatDuration(params.value) : '')
    },
    {
      field: 'synchronizableResults',
      headerName: 'Concepts',
      flex: 1,
      renderCell: params => {
        const synchronizables = params.row.synchronizables;
        // transform the synchronizable results into "result synchronizables" for rendering
        const resultSynchronizables = (params.value || []).map(synchronizableResult => {
          const synchronizable = synchronizables.find(
            synchronizable => synchronizable.uri === synchronizableResult.uri
          );
          return {
            ...synchronizableResult,
            vklQualifiedIdentifier: synchronizable.vklQualifiedIdentifier,
            vklType: synchronizable.vklType,
            // render "unknown" if the sync policy is not available, which is the case if the concept is missing or not
            // synchronizable, or for old results
            syncPolicy: synchronizableResult.syncPolicy || 'unknown',
            // render "unknown" if the duration is not available, which is the case for old results
            duration:
              // check the type of the duration to avoid rendering "unknown" for a duration of 0
              typeof synchronizableResult.duration === 'number' ? synchronizableResult.duration : 'unknown'
          };
        });
        const unsynchronizedSynchronizables = synchronizables
          .filter(
            synchronizable =>
              !resultSynchronizables.some(resultSynchronizable => resultSynchronizable.uri === synchronizable.uri)
          )
          .map(synchronizable => ({
            ...synchronizable,
            // set 'isSuccess' to 'null' s.t. the 'Synchronizable' component renders it grey
            isSuccess: null,
            // unset any other properties that might be set s.t. the 'Synchronizable' tooltip doesn't render them
            syncPolicy: undefined,
            duration: undefined,
            errorMessage: undefined
          }));
        const mergedSynchronizables = [...resultSynchronizables, ...unsynchronizedSynchronizables];
        return mergedSynchronizables.length ? renderSynchronizablesCell(mergedSynchronizables, 'Concepts') : 'None';
      }
    }
  ];

  // the rows for the data grid containing the synchronizations
  const synchronizationRows = (synchronizations || [])
    // if synchronization schedules are selected, only show the synchronizations associated to them
    .filter(
      synchronization =>
        scheduleSelectionModel.length === 0 ||
        scheduleSelectionModel.includes(synchronization.data.initiatorSynchronizationScheduleId)
    )
    .map(synchronization => ({
      id: synchronization.data.id,
      startDate: synchronization.data.startDate,
      type: synchronization.data.initiatorSynchronizationScheduleId ? 'scheduled' : 'manual',
      status: synchronization.data.status,
      synchronizables: synchronization.data.synchronizables,
      duration: synchronization.data.result?.duration,
      synchronizableResults: synchronization.data.result?.synchronizableResults,
      errorMessage: synchronization.data.result?.errorMessage
    }));

  const deleteResources = (deleteResource, ids, type, callback) => {
    const count = ids.length;
    const ifMultiple = (count, text) => (count > 1 ? text.replace('$', count) : '');
    const deleteResources = async () => {
      // trigger the deletion request(s) and wait for all of them to complete
      const responsesWithIds = await Promise.all(
        ids.map(async id => {
          const response = await deleteResource(id);
          return [response, id];
        })
      );
      // get the successful and failed responses and their counts
      const [successfulResponsesWithIds, failedResponsesWithIds] = partition(
        responsesWithIds,
        ([response]) => response.success
      );
      const successCount = successfulResponsesWithIds.length;
      const failureCount = count - successCount;

      // show success and/or error messages
      if (successCount) {
        const the = failureCount ? '' : 'The ';
        const count = ifMultiple(successCount, '$ ');
        successMessage(
          `${the}${count}${plrl(successCount, type)} ${plrl(successCount, 'has', 'have')} successfully been deleted`
        );
      }
      if (failureCount) {
        const the = successCount ? '' : 'The ';
        const count = ifMultiple(failureCount, '$ ');
        errorMessage(
          `${the}${count}${plrl(failureCount, type)} ${plrl(failureCount, 'has', 'have')} failed to be deleted`
        );
      }
      const successfulIds = successfulResponsesWithIds.map(([response, id]) => id);
      const failedIds = failedResponsesWithIds.map(([response, id]) => id);
      callback(successfulIds, failedIds);
    };

    showConfirmationModal(() => ({
      title: `Delete ${plrl(count, type)}`,
      content: `Do you really want to delete the ${ifMultiple(count, '$ ')}selected ${plrl(count, type)}?`,
      confirmButtonText: `Delete${ifMultiple(count, ' ($)')}`,
      onConfirm: deleteResources
    }));
  };

  const deleteSelectedSynchronizationSchedules = () =>
    deleteResources(
      id => services.deleteSynchronizationSchedule(kgId, id),
      scheduleSelectionModel,
      'scheduled synchronization',
      successfulIds => {
        setScheduleSelectionModel([]);
        reloadSchedules();
        reloadSynchronizations();
        successfulIds.forEach(id => trackEvent('Synchronization Schedule Deleted', { schedule: { id } }));
      }
    );

  const createSynchronization = (
    confirmationTitle,
    confirmationMessage,
    confirmationButtonLabel,
    synchronizables,
    callback
  ) => {
    const run = async () => {
      let response = null;
      // create (run) the synchronization without waiting for the synchronization to complete
      services.createSynchronization(kgId, synchronizables).then(r => (response = r));
      // wait to allow for the request to fail or for the synchronization to start (and be persisted to the database)
      await sleep(2000);
      // check if the request has failed during the first 2 seconds
      if (response && !response.success) {
        const message = response.response.data.data?.message || 'There was an error starting the synchronization';
        errorMessage(message);
      } else {
        successMessage('The synchronization has successfully been started');
      }
      // reload the synchronizations in any case
      await reloadSynchronizations();
      // call the callback, if specified
      if (callback) {
        callback();
      }
    };

    const content = (
      <>
        <div>{confirmationMessage}</div>
        {synchronizables ? (
          <>
            <p>The following concepts will be synchronized:</p>
            <div className={classes.confirmationSynchronizables}>
              {synchronizables.map(synchronizable => (
                <Synchronizable key={synchronizable.uri} synchronizable={synchronizable} />
              ))}
            </div>
          </>
        ) : (
          <p>
            All concepts which can be synchronized and which have a sync policy other than <i>ignore</i> will be
            synchronized
          </p>
        )}
      </>
    );
    showConfirmationModal(() => ({
      title: confirmationTitle,
      content,
      confirmButtonText: confirmationButtonLabel,
      onConfirm: run
    }));
  };

  return (
    <Content ref={contentRef} loading={!schedules || !synchronizations} data-test="synchronizationContent">
      <PageTitle icon={SyncIcon}>Synchronization</PageTitle>
      <Typography variant="body1" gutterBottom>
        Synchronization allows you to keep the entities in the Knowledge Graph in sync with the individual values of
        your database. This is important so that Veezoo can understand references to them in a question. <br />
        <Link href={veezooUrls.syncDocs} target="_blank" rel="noreferrer">
          Learn more about it in our docs.
        </Link>
      </Typography>

      <Typography variant="h2" gutterBottom>
        Scheduled Synchronizations
      </Typography>
      <Typography variant="body1" gutterBottom>
        View, manage and run scheduled synchronizations.
      </Typography>

      <DataGrid
        className="schedulesDatagrid"
        columns={scheduleColumns}
        rows={scheduleRows}
        sortModel={[{ field: 'nextScheduledDate', sort: 'asc' }]}
        checkboxSelection
        selectionModel={scheduleSelectionModel}
        onSelectionModelChange={newSelectionModel => {
          // TODO: This verification is needed, because on React-17 the selectionModel is an object with a selectionModel property,
          // while on React-18 the selectionModel is an array of ids.
          // As soon as we upgrade to React-18, we can remove this verification.
          const newSelection = newSelectionModel.selectionModel || newSelectionModel;
          setScheduleSelectionModel(newSelection);
        }}
        actions={[
          {
            label: 'Schedule',
            icon: <AddIcon />,
            tooltip: 'Schedule a new synchronization',
            onClick: openScheduleDialog
          },
          {
            label: 'Edit',
            icon: <EditIcon />,
            tooltip:
              scheduleSelectionModel.length === 1
                ? 'Edit the selected scheduled synchronization'
                : 'Select a single scheduled synchronization to edit',
            disabled: scheduleSelectionModel.length !== 1,
            onClick: () => openScheduleDialog(scheduleSelectionModel[0])
          },
          {
            label: 'Delete',
            icon: <DeleteIcon />,
            color: 'secondary',
            tooltip:
              scheduleSelectionModel.length > 0
                ? `Delete the selected scheduled ${plrl(scheduleSelectionModel.length, 'synchronization')}`
                : 'Select one or more scheduled synchronizations to delete',
            disabled: scheduleSelectionModel.length === 0,
            onClick: deleteSelectedSynchronizationSchedules
          },
          {
            label: 'Run now',
            icon: <PlayArrowIcon />,
            tooltip:
              scheduleSelectionModel.length === 1
                ? 'Run the selected scheduled synchronization now'
                : 'Select a single scheduled synchronization to run it now',
            disabled: scheduleSelectionModel.length !== 1,
            onClick: () => {
              const schedule = schedules.find(schedule => schedule.data.id === scheduleSelectionModel[0]);
              createSynchronization(
                'Run scheduled synchronization now',
                'Do you really want to run the selected scheduled synchronization now?',
                'Run now',
                schedule.data.synchronizables,
                () => setScheduleSelectionModel([])
              );
            }
          }
        ]}
        noRowsText="There are no scheduled synchronizations"
      />

      <Typography variant="h2" gutterBottom>
        Synchronizations
      </Typography>

      <Typography variant="body1" gutterBottom>
        View past synchronizations and rerun them.
        {scheduleSelectionModel.length > 0 &&
          ` The table is showing all past runs for the selected scheduled ${plrl(
            scheduleSelectionModel,
            'synchronization'
          )}.`}
      </Typography>
      <DataGrid
        className="synchronizationsDatagrid"
        columns={synchronizationColumns}
        rows={synchronizationRows}
        sortModel={[{ field: 'startDate', sort: 'desc' }]}
        checkboxSelection
        selectionModel={synchronizationSelectionModel}
        onSelectionModelChange={newSelectionModel => {
          // TODO: This verification is needed, because on React-17 the selectionModel is an object with a selectionModel property,
          // while on React-18 the selectionModel is an array of ids.
          // As soon as we upgrade to React-18, we can remove this verification.
          const newSelection = newSelectionModel.selectionModel || newSelectionModel;
          setSynchronizationSelectionModel(newSelection);
        }}
        actions={[
          {
            label: 'Refresh',
            icon: <SyncIcon />,
            tooltip: 'Refresh the synchronizations table',
            onClick: reloadSynchronizations
          },
          {
            label: 'Rerun',
            icon: <PlayArrowIcon />,
            tooltip:
              synchronizationSelectionModel.length === 1
                ? 'Rerun the selected synchronization again'
                : 'Select a single synchronization to rerun',
            disabled: synchronizationSelectionModel.length !== 1,
            onClick: () => {
              const synchronization = synchronizations.find(
                synchronization => synchronization.data.id === synchronizationSelectionModel[0]
              );
              createSynchronization(
                'Rerun synchronization',
                'Do you really want to rerun the selected synchronization?',
                'Rerun',
                synchronization.data.synchronizables,
                () => setSynchronizationSelectionModel([])
              );
            }
          }
        ]}
        noRowsText={
          scheduleSelectionModel.length > 0
            ? `There are no past runs yet for the selected scheduled ${plrl(scheduleSelectionModel, 'synchronization')}`
            : 'No synchronizations have run yet'
        }
      />

      <Dialog maxWidth="md" open={showScheduleDialog} onClose={closeScheduleDialog}>
        <DialogTitle>{scheduleToEdit ? 'Edit scheduled synchronization' : 'Schedule new synchronization'}</DialogTitle>
        <DialogContent>
          <div style={{ width: 700 }}>
            <ScheduleForm
              knowledgeGraph={knowledgeGraph}
              schedule={scheduleToEdit}
              onCancel={closeScheduleDialog}
              onSuccess={() => {
                closeScheduleDialog();
                reloadSchedules();
                setScheduleSelectionModel([]);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </Content>
  );
};

export default withStyles(styles)(Synchronization);
